// @ts-nocheck
import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../hooks/useRedux";
import {
  createUserPost,
  startUploadingPost,
  updateUploadProgress,
} from "../store/postsSlice";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import WarningModal from "../components/WarningModal";

const CreatePostScreen = () => {
  const [media, setMedia] = useState(null);
  const [caption, setCaption] = useState("");
  const [showModal, setShowModal] = useState(false);
  const dispatch = useAppDispatch();
  const { loading } = useAppSelector((state) => state.posts);
  const user = useAppSelector((state) => state.user);
  const [isPosting, setIsPosting] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (user.user_id) {
      const hasSeenModal = localStorage.getItem(
        `hasSeenWarningModal_${user.user_id}`
      );
      if (!hasSeenModal) {
        setShowModal(true);
      }
    }
  }, [user.user_id]);

  const handleAgree = () => {
    localStorage.setItem(`hasSeenWarningModal_${user.user_id}`, "true");
    setShowModal(false);
  };

  const handleCancel = () => {
    setShowModal(false);
    navigate("/feed");
  };

  const handlePickMedia = () => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*,video/*";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) {
        toast.error("No file selected.", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      const fileType = file.type.startsWith("image")
        ? "image"
        : file.type.startsWith("video")
        ? "video"
        : null;
      if (!fileType) {
        toast.error("Invalid file type. Please select an image or video.", {
          position: "top-right",
          autoClose: 3000,
        });
        return;
      }

      const maxSizeInMB = 200;
      const fileSizeInMB = file.size / (1024 * 1024);
      if (fileType === "video" && fileSizeInMB > maxSizeInMB) {
        toast.error(
          `Video too large. Please select a video under ${maxSizeInMB} MB.`,
          {
            position: "top-right",
            autoClose: 3000,
          }
        );
        return;
      }

      setMedia({
        uri: URL.createObjectURL(file),
        type: fileType,
        file,
      });
    };
    input.click();
  };

  const handlePost = async () => {
    if (!media) {
      toast.error("Please select an image or video.", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setIsPosting(true);
    const tempId = `temp-${Date.now()}`;
    const isImage = media.type === "image";
    const finalUri = media.uri;
    const finalMime = isImage ? "image/jpeg" : "video/mp4";
    const fileName = media.file.name || "upload";

    const file = {
      uri: finalUri,
      name: fileName,
      type: finalMime,
    };

    dispatch(
      startUploadingPost({
        tempId,
        progress: 0,
        caption,
        mediaType: isImage ? "image" : "video",
        fileName: file.name,
      })
    );

    try {
      const result = await dispatch(
        createUserPost({
          caption,
          mediaFile: media.file,
          mediaType: isImage ? "image" : "video",
          tempId,
          onProgress: (progress) => {
            dispatch(updateUploadProgress({ tempId, progress }));
          },
        })
      );

      if (createUserPost.fulfilled.match(result)) {
        toast.success("Post created successfully!", {
          position: "top-right",
          autoClose: 3000,
        });
        setTimeout(() => {
          navigate("/feed");
          setCaption("");
          setMedia(null);
        }, 500);
      } else {
        toast.error(result.payload || "Something went wrong", {
          position: "top-right",
          autoClose: 3000,
        });
      }
    } catch (error) {
      toast.error("Post creation failed. Something went wrong.", {
        position: "top-right",
        autoClose: 3000,
      });
    } finally {
      setIsPosting(false);
    }
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <WarningModal
        visible={showModal}
        onAgree={handleAgree}
        onCancel={handleCancel}
      />
      <div className="min-h-screen bg-black p-5 flex flex-col max-w-screen-md mx-auto">
        <h2 className="text-white text-2xl font-bold text-center mb-5">
          Create Post
        </h2>

        <button
          className="border-2 border-white rounded-lg h-80 flex justify-center items-center mb-5 overflow-hidden bg-gray-300"
          onClick={handlePickMedia}
        >
          {media ? (
            media.type === "image" ? (
              <img
                src={media.uri}
                alt="Media Preview"
                className="w-full h-full object-contain"
              />
            ) : (
              <video
                src={media.uri}
                className="w-full h-full object-cover"
                controls
              />
            )
          ) : (
            <div className="text-black text-base">
              Tap to choose image or video
            </div>
          )}
        </button>

        <textarea
          className="h-24 border border-white rounded-lg p-3 text-black bg-gray-300 placeholder-black mb-5 resize-none"
          placeholder="Write a caption..."
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              document.activeElement.blur();
            }
          }}
        />

        <button
          className="bg-red-600 text-white py-3 rounded-lg hover:bg-red-700 transition disabled:opacity-50"
          onClick={handlePost}
          disabled={isPosting}
        >
          {isPosting ? (
            <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-white mx-auto"></div>
          ) : (
            <span className="text-base font-semibold">Post</span>
          )}
        </button>
      </div>
    </>
  );
};

export default CreatePostScreen;
